import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'

import { PageBar, Content } from '../components/Layout'
import { Badge, Empty, ErrorBox, Loading, Panel } from '../components/ui'
import { useEffect, useRef, useState } from 'react'
import { api } from '../lib/api'
import { shortDate, titleise, waited } from '../lib/format'
import type { AppSettings, EnquiryList } from '../types'

const TABS = [
  ['inbox', 'Inbox'],
  ['client', 'Client'],
  ['education', 'Education'],
  ['needs_triage', 'Needs triage'],
  ['spam', 'Spam'],
  ['all', 'All'],
] as const

export function Enquiries() {
  const [params, setParams] = useSearchParams()
  const queryClient = useQueryClient()
  const tab = params.get('tab') ?? 'inbox'

  const { data, isLoading, error } = useQuery({
    queryKey: ['enquiries', tab],
    queryFn: () => api.get<EnquiryList>(`/enquiries?tab=${tab}`),
  })
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => api.get<AppSettings>('/settings') })
  const sla = settings.data?.sla_hours ?? 4

  const [explain, setExplain] = useState(false)

  // On a phone the tabs are one row that scrolls, so the active one can start off-screen
  // - arrive on ?tab=all and nothing looks selected, which reads as broken. Pull it into
  // view. `nearest` on both axes so this never scrolls the page itself, and the optional
  // call because jsdom has no scrollIntoView.
  const tabsRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    tabsRef.current
      ?.querySelector('.tab.on')
      ?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
  }, [tab])

  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/enquiries/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enquiries'] })
      queryClient.invalidateQueries({ queryKey: ['overview'] })
    },
  })

  // Stands in for WordPress and the mailbox until those are wired up. Runs the real
  // triage path, so what appears is the system working rather than a fixture.
  const simulate = useMutation({
    mutationFn: () => api.post('/simulate/enquiry'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enquiries'] })
      queryClient.invalidateQueries({ queryKey: ['overview'] })
    },
  })

  return (
    <>
      <PageBar
        title="Enquiries"
        actions={
          <>
            <button className="btn ghost" type="button" onClick={() => setExplain((v) => !v)}>
              {explain ? 'Hide' : 'How this works'}
            </button>
            <button
              className="btn ghost"
              type="button"
              disabled={simulate.isPending}
              onClick={() => simulate.mutate()}
              title="Push a realistic message through the real ingest path"
            >
              {simulate.isPending ? 'Receiving…' : 'Simulate inbound'}
            </button>
          </>
        }
      />
      <Content>
        {explain && (
          <div className="explain">
            <h3>Where these come from</h3>
            <p>
              Someone fills in the form at <code>oldstreetstudios.com/contact</code>. WordPress
              still emails <b>bookings@oldstreetstudios.com</b> exactly as it always has — nothing
              about that changed. It now <i>also</i> posts a copy here, so the same enquiry is in
              the inbox and in this list.
            </p>

            <h3>What the tabs mean</h3>
            <ul>
              <li>
                <b>Inbox</b> — live enquiries a human still needs to deal with. This is the only
                number that should ever worry you.
              </li>
              <li>
                <b>Client · Education · Needs triage · Spam</b> — every enquiry sorted by what it
                looks like, including five years of imported history.
              </li>
              <li><b>All</b> — everything, live and historical.</li>
            </ul>

            <h3>The percentage next to the category</h3>
            <p>
              How sure the classifier is. Above 70% it files the enquiry itself. Below that it
              says <b>needs triage</b> and leaves it for a person — being unsure out loud is the
              point. A real booking enquiry sitting at 65% is the system doing its job, not
              failing.
            </p>
            <p>
              Spam and tests are archived the moment they arrive, so they never reach anyone's
              phone. That is why a test submission does not show up in the inbox.
            </p>

            <h3>Why the imported ones are not "waiting"</h3>
            <p>
              The {data?.history ?? 453} historical enquiries came from a spreadsheet export.
              Nothing in it records whether anyone ever replied, so counting them as waiting would
              put a confidently wrong number on the dashboard. They are archived and searchable
              instead.
            </p>

            <h3>Deleting</h3>
            <p>
              <b>Delete</b> removes an enquiry and its messages permanently. It is there because
              these are real people and UK data protection gives them a right to be erased on
              request — archiving is not erasing. It is also how you clear out test rows.
            </p>
          </div>
        )}

        <div className="tabs" ref={tabsRef}>
          {TABS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`tab ${tab === key ? 'on' : ''}`}
              onClick={() => setParams({ tab: key })}
            >
              {label}
              <span className="n">{data?.counts[key] ?? 0}</span>
            </button>
          ))}
        </div>

        {/* A delete that fails has to say so. Without this the button simply does
            nothing on a failure, which is indistinguishable from a dead button - and
            the one person who can report it usefully is looking at a phone, not a log. */}
        {remove.error && (
          <div className="note bad">
            <b>That enquiry was not deleted.</b> <ErrorBox error={remove.error} />
          </div>
        )}

        <Panel>
          {isLoading && <Loading />}
          {error && <ErrorBox error={error} />}
          {data && !data.enquiries.length && <Empty>Nothing here.</Empty>}
          {data && data.enquiries.length > 0 && (
            <table className="cards">
              <thead>
                <tr>
                  <th style={{ width: 96 }}>Received</th>
                  <th style={{ width: 200 }}>From</th>
                  <th>Message</th>
                  <th style={{ width: 120 }}>Category</th>
                  <th style={{ width: 110 }} className="hide-sm">Segment</th>
                  <th style={{ width: 92 }} className="hide-sm">Waiting</th>
                  <th style={{ width: 70 }} />
                </tr>
              </thead>
              <tbody>
                {data.enquiries.map((e) => (
                  <tr key={e.id} className={e.source === 'simulated' ? 'fake' : undefined}>
                    <td className="when">{shortDate(e.received_at)}</td>
                    <td>
                      <Link to={`/enquiries/${e.id}`}>
                        <div className="who">
                          {e.name || 'Unknown'}
                          {/* The simulator invents convincing people on purpose. Say so on
                              the row itself, or the only way to tell Dr Alice Venn from a
                              real enquiry is to email her and find out. */}
                          {e.source === 'simulated' && <span className="faketag">Not real</span>}
                        </div>
                        <div className="email">{e.email}</div>
                      </Link>
                    </td>
                    <td className="snippet">
                      <Link to={`/enquiries/${e.id}`}>
                        {e.message.slice(0, 120)}
                        {e.message.length > 120 && '…'}
                      </Link>
                    </td>
                    <td>
                      <Badge kind="b" value={e.category} />
                      {e.confidence > 0 && <div className="conf">{Math.round(e.confidence * 100)}%</div>}
                    </td>
                    <td className="hide-sm">
                      {e.segment !== 'unknown'
                        ? <Badge kind="seg" value={e.segment} />
                        : <span className="muted">—</span>}
                    </td>
                    <td className="hide-sm">
                      {e.first_reply_at ? (
                        <span className="wait ok">replied</span>
                      ) : e.status === 'archived' ? (
                        <span className="wait">—</span>
                      ) : (
                        <span className={`wait ${e.hours_waiting && e.hours_waiting > sla ? 'late' : ''}`}>
                          {waited(e.hours_waiting)}
                        </span>
                      )}
                    </td>
                    <td className="rowactions">
                      <button
                        type="button"
                        className="tab danger"
                        disabled={remove.isPending && remove.variables === e.id}
                        onClick={() => {
                          const who = e.name || e.email || 'this enquiry'
                          if (window.confirm(`Permanently delete ${who}?\n\nThe enquiry and every message go with it. This cannot be undone.`)) {
                            remove.mutate(e.id)
                          }
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        {data && (
          <div className="note">
            <b>{data.history} imported enquiries</b> from the 2021–2026 form export are archived and
            browsable under the category tabs. They are deliberately <i>not</i> counted as waiting: we
            have no record of whether they were ever answered, and a confidently wrong number is worse
            than none.
          </div>
        )}
        <p className="hint">{titleise('')}</p>
      </Content>
    </>
  )
}
